import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import * as styles from './actionsQuantityModal.module.scss'
import { Row, Col } from 'react-grid-system'
import { AiOutlinePlusCircle, AiOutlineMinusCircle } from 'react-icons/ai'

import { Button, Image, Modal } from '../common'
import { truncate } from '../../utils/functions'
import { ActionsQuantityModalProps } from './actionsWorkflow.type'
import { Item } from '../../types'

const ActionsQuantityModal = ({
   isModalOpen,
   handleClose,
   handleAdjustTotal,
   handleCustomTotalChange,
   handleAddQuantityItemsToCart,
   getQuantityItemsFromPage,
   items,
   isEdit,
   selectedActionQuickFilter,
}: ActionsQuantityModalProps) => {
   const [itemInitialQuantity, setItemInitialQuantity] = useState<
      number | null
   >(null)

   const isWarehouseTransfer = useMemo(
      () => selectedActionQuickFilter?.label === 'Warehouse Transfer',
      [selectedActionQuickFilter]
   )

   useEffect(() => {
      if (isEdit) {
         setItemInitialQuantity(items[0].quantityForCart)
      }
   }, [])

   const isItemQuantityValid = useCallback(
      (item: Item) =>
         item.quantityForCart > 0 &&
         item.quantityForCart <= item?.StatusQuantity &&
         !isNaN(item.quantityForCart),
      [isWarehouseTransfer]
   )

   const isQuantitiesValid = useMemo(
      () =>
         getQuantityItemsFromPage(items).every((item) =>
            isItemQuantityValid(item)
         ),
      [items, getQuantityItemsFromPage, isItemQuantityValid]
   )

   const getInputClassName = useCallback(
      (isValid: boolean) =>
         isValid ? styles.quantityInput : styles.quantityInputInvalid,
      []
   )

   const handleEditCancel = () => {
      items[0].quantityForCart = itemInitialQuantity // if in edit mode set item quantity back to last known state
      handleClose()
   }

   return (
      <Modal
         isModalVisible={isModalOpen}
         closeModal={handleClose}
         title="Select Item Quantities"
         zIndex="999"
         stickyHeader
      >
         <div className={styles.warningModalMessageWrapper}>
            <p>How many of the following items should be added to your cart?</p>
         </div>

         <section className={styles.contentWrapper}>
            {getQuantityItemsFromPage(items).map((item, i) => {
               const isValid = isItemQuantityValid(item)

               return (
                  <div key={item.localUniqueId + '_' + i}>
                     <section className={styles.itemRow}>
                        <div className={styles.totalSelector}>
                           <button
                              type="button"
                              className={styles.adjustAmountButton}
                              onClick={() => handleAdjustTotal(item, 'dec')}
                              title="Decrease Quantity"
                           >
                              <AiOutlineMinusCircle size={25} />
                           </button>
                           <input
                              className={getInputClassName(isValid)}
                              type="number"
                              id="quantity"
                              name="quantity"
                              min="1"
                              max="10000"
                              value={item.quantityForCart}
                              onChange={(e) => handleCustomTotalChange(e, item)}
                           ></input>
                           <button
                              type="button"
                              className={styles.adjustAmountButton}
                              onClick={() => handleAdjustTotal(item, 'inc')}
                              title="Increase Quantity"
                           >
                              <AiOutlinePlusCircle size={25} />
                           </button>
                        </div>
                        <span className={styles.availableTotal}>
                           {`${item.StatusQuantity} Available`}
                        </span>
                        <span className={styles.quantityImage}>
                           <Image
                              url={item.PictureURL}
                              alt={item.PictureURL}
                              defaultImagePadding={2}
                              width={30}
                           />
                        </span>
                        <span className={styles.itemNameSection}>
                           {truncate(item.Title, 45)}
                        </span>
                     </section>
                     {!isValid && (
                        <p className={styles.invalidWarning}>
                           Invalid quantity
                        </p>
                     )}
                  </div>
               )
            })}
         </section>
         <div className={styles.buttonRow}>
            <Row>
               <Col xs={12} sm={6} md={6} lg={6}>
                  <div className={styles.buttonWrapper}>
                     <Button
                        variant={'primary'}
                        onClick={
                           isEdit
                              ? handleClose
                              : () => handleAddQuantityItemsToCart(items)
                        }
                        type="button"
                        isReset
                        disabled={!isQuantitiesValid}
                     >
                        {isEdit ? 'Update Cart' : 'Add To Cart'}
                     </Button>
                  </div>
               </Col>
               <Col xs={12} sm={6} md={6} lg={6}>
                  <div className={styles.buttonWrapper}>
                     <Button
                        variant={'tertiary'}
                        onClick={isEdit ? handleEditCancel : handleClose}
                        type="button"
                        isReset
                     >
                        Cancel
                     </Button>
                  </div>
               </Col>
            </Row>
         </div>
      </Modal>
   )
}

export default memo(ActionsQuantityModal)
